/**
 * Alert: <uo-alert>
 */
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
// Import variables
import './variables.scss';
import { unsafeCSS, css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
// Import global utils
import { isEmpty } from '@automator/shared/utils/is-empty';
// Import style for web component
import styles from './web-component-style.scss?inline';
/**
 * @element uo-alert
 * @summary Displays a styled alert message block with optional icon, heading, and type styling.
 * @description This component renders an alert box suitable for displaying messages like warnings, errors, success notifications, or general information.
 * It supports customization through properties/attributes and content projection via slots.
 *
 * @slot - The main content/body of the alert. Typically paragraph text.
 * @slot icon - Custom content to be placed in the icon area. If used, overrides the default icon associated with the `type`, unless `customIcon` is also true.
 * @slot top-right-icon - Custom content placed in the top-right corner of the heading area (e.g., a close button).
 *
 * @attr {boolean} no-radius - Removes the default border radius when present. Maps to the `noRadius` property.
 * @attr {boolean} custom-icon - Indicates that the 'icon' slot content handles its own styling and layout. Maps to the `customIcon` property.
 * @attr {'small' | 'medium' | 'large'} [size] - Specifies a size variant for the alert (e.g., 'small', 'medium', 'large'). Maps to the `size` property.
 * @attr {string} [heading] - Text for the alert's heading. Supports basic markdown (`<strong>`, `<em>`). Maps to the `heading` property.
 * @attr {'warning' | 'error' | 'success' | 'info' | 'white'} [type] - Defines the alert's style type (e.g., 'warning', 'error'). Maps to the `type` property.
 *
 * @cssprop {string} [--uap-alert-padding=15px] - Default padding inside the alert container.
 * @cssprop {string} [--uap-alert-small-padding=10px] - Padding used when the alert `size` is "small".
 * @cssprop {string} [--uap-alert-xsmall-padding=8px] - Padding used when the alert `size` is "xsmall".
 * @cssprop {string} [--uap-alert-icon-gap=10px] - Sets the gap between the icon area and the alert body.
 * @cssprop {string} [--uap-alert-default-background=#f3f3f3] - Background color for the default alert type (when no `type` or an unhandled `type` is specified).
 * @cssprop {string} [--uap-alert-warning-background=var(--uap-warning-background)] - Background color when `type` is "warning". Falls back to the global warning background.
 * @cssprop {string} [--uap-alert-error-background=var(--uap-error-background)] - Background color when `type` is "error". Falls back to the global error background.
 * @cssprop {string} [--uap-alert-success-background=var(--uap-success-background)] - Background color when `type` is "success". Falls back to the global success background.
 * @cssprop {string} [--uap-alert-info-background=var(--uap-info-background)] - Background color when `type` is "info". Falls back to the global info background.
 * @cssprop {string} [--uap-alert-white-background=#fff] - Background color when `type` is "white".
 * @cssprop {string} [--uap-alert-style-heading-custom-color=var(--uap-color-primary)] - Overrides the text color specifically for `<strong>` tags within the alert heading.
 */
let AutomatorAlert = class AutomatorAlert extends LitElement {
    constructor() {
        super(...arguments);
        /**
         * If `true`, removes the default border radius from the alert container.
         * @attr no-radius
         * @default false
         */
        this.noRadius = false;
        /**
         * If `true`, the component assumes the content slotted into `icon` handles its own styling and layout, bypassing default icon styles.
         * @attr custom-icon
         * @default false
         */
        this.customIcon = false;
    }
    /**
     * Renders the component's DOM structure based on its properties and slotted content.
     * @return {TemplateResult} The HTML template for the alert component.
     */
    render() {
        return html `
			<div
				class="alert"
				type=${ifDefined(this.type)}
				size=${ifDefined(this.size)}
				?no-radius=${this.noRadius}
				?custom-icon=${this.customIcon}
			>
				<div class="alert-icon">
					<slot name="icon"></slot>
				</div>

				<div class="alert-body">
					${this.$heading}

					<div class="alert-content">
						<slot></slot>
					</div>
				</div>
			</div>
		`;
    }
    /**
     * Generates the `TemplateResult` for the alert's heading section.
     * This includes the heading text processed by `uo-markdown` and the `top-right-icon` slot.
     * Returns `undefined` if no `heading` property is set, effectively hiding the heading section.
     * @return {TemplateResult | undefined} The template for the heading or undefined.
     * @protected
     */
    get $heading() {
        // Check if there is a heading defined
        if (isEmpty(this.heading)) {
            // Return undefined, Lit will render nothing for this placeholder
            return;
        }
        return html `
			<div class="alert-heading">
				<div class="alert-heading-text">
					<uo-markdown
						.content=${this.heading}
						allowed-tags="strong,em"
					></uo-markdown>
				</div>

				<div class="alert-heading-icon">
					<slot name="top-right-icon"></slot>
				</div>
			</div>
		`;
    }
};
/**
 * Applies the component's styles, loading from the imported SCSS file.
 * Uses `unsafeCSS` to allow Lit to process the raw CSS string.
 */
AutomatorAlert.styles = css `${unsafeCSS(styles)}`;
__decorate([
    property({ type: Boolean, attribute: 'no-radius' })
], AutomatorAlert.prototype, "noRadius", void 0);
__decorate([
    property({ type: Boolean, attribute: 'custom-icon' })
], AutomatorAlert.prototype, "customIcon", void 0);
__decorate([
    property({ type: String })
], AutomatorAlert.prototype, "size", void 0);
__decorate([
    property({ type: String })
], AutomatorAlert.prototype, "heading", void 0);
__decorate([
    property({ type: String })
], AutomatorAlert.prototype, "type", void 0);
AutomatorAlert = __decorate([
    customElement('uo-alert')
], AutomatorAlert);
export { AutomatorAlert };
